import type { JSX } from 'react';
import { useLocale } from '@gs/i18n/localeContext';
import { useWhatsNew } from '../hooks/useWhatsNew';
import { GsDrawer } from './GsDrawer';

// Copie interne FR/EN (précédent GsBuildStamp) : le MÊME libellé dans toutes les apps, sans une
// clé de catalogue par app. Zéro t() dans @gs/ui.
const COPY = {
  fr: {
    link: 'Quoi de neuf',
    title: 'Quoi de neuf',
    subtitle: 'Ce qui a changé à chaque mise en production',
    close: 'Fermer',
    locale: 'fr-CA',
  },
  en: {
    link: "What's new",
    title: "What's new",
    subtitle: 'What changed with each release',
    close: 'Close',
    locale: 'en-CA',
  },
} as const;

function useCopy() {
  const localeCtx = useLocale();
  const lang = localeCtx?.locale === 'en' ? 'en' : 'fr';
  return { lang, copy: COPY[lang] } as const;
}

/** `2026-09-30` -> « 30 septembre 2026 » / « September 30, 2026 » ; illisible -> tel quel. */
function formaterDate(date: string, locale: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
}

export interface GsWhatsNewLinkProps {
  /** Ouvre le tiroir (`GsWhatsNewDrawer`). */
  onOpen: () => void;
  /** Séparateur rendu AVANT le lien, seulement s il s affiche (` · ` à côté de la version). */
  lead?: string;
}

/**
 * Le lien « Quoi de neuf » à côté du numéro de version (lot feat-quoi-de-neuf-par-app).
 * Ne rend rien tant que la lecture n est pas revenue, et quand l app n a aucun passage relu :
 * jamais un lien mort.
 */
export function GsWhatsNewLink({ onOpen, lead }: GsWhatsNewLinkProps): JSX.Element | null {
  const passages = useWhatsNew();
  const { copy } = useCopy();
  if (!passages || passages.length === 0) return null;

  return (
    <>
      {lead}
      <button
        type="button"
        data-gs-whats-new-link=""
        onClick={onOpen}
        className="font-medium underline underline-offset-2 hover:text-slate-700 dark:hover:text-slate-200 touch-manipulation"
      >
        {copy.link}
      </button>
    </>
  );
}

export interface GsWhatsNewDrawerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Le tiroir des nouveautés : un bloc par passage (date, puis les lignes), le plus récent d abord.
 * `GsDrawer` est en position fixe : le pied de la barre latérale ne le rogne pas (aucun
 * `transform` sur la barre). Pas de portail : `react-dom` n est pas une dépendance de `@gs/ui`.
 */
export function GsWhatsNewDrawer({ open, onClose }: GsWhatsNewDrawerProps): JSX.Element | null {
  const passages = useWhatsNew();
  const { lang, copy } = useCopy();
  if (!open || !passages || passages.length === 0) return null;

  return (
    <GsDrawer open onClose={onClose} title={copy.title} subtitle={copy.subtitle} closeLabel={copy.close}>
      <div data-gs-whats-new="" className="space-y-5">
        {passages.map((p) => (
          <section key={p.etiquette}>
            <h3 className="text-meta font-semibold text-slate-500 dark:text-slate-400">
              {formaterDate(p.date, copy.locale)}
            </h3>
            <ul className="mt-1.5 list-disc space-y-1.5 pl-4 text-body text-slate-800 dark:text-slate-100">
              {p.lignes.map((l) => (
                <li key={l.fr}>{lang === 'en' ? (l.en ?? l.fr) : l.fr}</li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </GsDrawer>
  );
}
